import { LitElement, css, html } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { when } from "lit/directives/when.js";
import { dlStyles } from "../../../styles";
import type { Package } from "../entities";

@customElement("umb-market-package-sidebar-install-info")
export class UmbMarketPackageSidebarInstallInfo extends LitElement {
  @property({ type: Object })
  package?: Package;

  @state()
  noNuget = false;

  @state()
  action = "add package";

  @state()
  supportsGreaterThan8 = false;

  @state()
  source: 'dotnet' | 'npm' = 'dotnet';

  connectedCallback(): void {
    super.connectedCallback();

    if (!this.package) return;

    this.supportsGreaterThan8 =
      Math.max(...this.package.umbracoMajorVersionsSupported) !== 8;

    if (this.package.packageSource === "NPM") {
      this.noNuget = true;
      this.source = 'npm';
      this.action = "install";
    } else if (this.package.packageType === "Tool") {
      this.noNuget = true;
      this.action = "tool install --global";
    } else if (this.package.packageType === "Template") {
      this.noNuget = true;
      this.action = "new install";
    }
  }

  getPackageTitleWithFlags(greaterThan8: boolean) {
    if (!this.package) return "";

    const packageId =
      this.package.packageIdForProvidedUmbracoVersion ?? this.package.packageId;

    if (!this.package.latestVersionNumberForProvidedUmbracoVersion) {
      return this.package?.packageId ?? "";
    }

    if (this.package.packageType === "Template") {
      return `${packageId}::${this.package.latestVersionNumberForProvidedUmbracoVersion}`;
    }

    // eslint-disable-next-line no-nested-ternary
    const versionFlag =
      this.package.packageType === "Tool"
        ? "--version"
        : greaterThan8
        ? "-v"
        : "-RequiredVersion";

    return `${packageId} ${versionFlag} ${this.package.latestVersionNumberForProvidedUmbracoVersion}`;
  }

  render() {
    if (!this.package) return null;

    return html`<div>
      <dl>
        ${when(
          this.package.isRetired,
          () => html`<umb-market-retired-package-toast
            .package=${this.package!}
          ></umb-market-retired-package-toast>`,
          () => html` <div>
            <dt>Install</dt>
            <dd>
              ${when(
                this.supportsGreaterThan8,
                () => html`<umb-market-package-toast
                  type=${this.source}
                  action=${this.action}
                  .title=${this.getPackageTitleWithFlags(true)}
                ></umb-market-package-toast>`
              )}
              ${when(
                !this.noNuget,
                () => html` <umb-market-package-toast
                  type="nuget"
                  .title=${this.getPackageTitleWithFlags(false)}
                ></umb-market-package-toast>`
              )}
            </dd>
          </div>`
        )}
      </dl>
    </div>`;
  }

  static styles = [
    dlStyles,
    css`
      :host {
        display: block;
      }

      dt {
        margin-bottom: var(--xs-spacer);
      }
    `,
  ];
}
